import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AtAGlance from '@/components/ui/AtAGlance';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1120SInstruction = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1120-S Instructions 2025 | Complete Filing Guide"
          description="Stay compliant with IRS Form 1120-S. Get expert tips, filing checklists, and tools to simplify your S Corporation tax return with our complete guide."
          keywords=""
          canonical="resources/business-tax/form-1120-s-instructions/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="1658"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          IRS{' '}
          <span className="highlight-container">
            <span className="highlight"> Form 1120-S</span>
          </span>{' '}
          Instructions: Complete{' '}
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>{' '}
          Guide for S Corporations
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Easily file IRS Form 1120-S and ensure your S Corporation stays
          compliant with IRS requirements.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120sinstructions`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
        component="article"
        itemScope
        itemType="https://schema.org/Article"
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 2,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ mb: 1, textAlign: 'right' }}
                >
                  <Typography variant="body2">
                    Last updated:{' '}
                    <time
                      itemProp="datePublished"
                      dateTime="2026-08-25T09:00:00-05:00"
                    >
                      Aug 25, 2026
                    </time>
                    <time
                      itemProp="dateModified"
                      dateTime="2026-08-25T09:00:00-05:00"
                      style={{ display: 'none' }}
                    >
                      Aug 25, 2026
                    </time>
                  </Typography>

                  <address
                    rel="author"
                    itemProp="author"
                    itemScope
                    itemType="https://schema.org/Person"
                    style={{ fontSize: 14, fontWeight: 600 }}
                  >
                    - by{' '}
                    <span itemProp="name">
                      <a
                        itemProp="url"
                        href="https://www.taxzerone.com/"
                        style={{
                          color: 'inherit',
                          textDecoration: 'none',
                        }}
                      >
                        Nikhil Chowdary
                      </a>
                    </span>{' '}
                    | TaxZerone
                  </address>
                </Stack>

                <AtAGlance
                  hrefUrl={`${process.env.NEXT_PUBLIC_HOMESITE_URL}resources/business-tax/form-1120-s-instructions/`}
                  headerText="Form 1120-S at a Glance"
                >
                  <Typography
                    component="p"
                    itemProp="description"
                    variant="body1"
                  >
                    <strong style={{ fontWeight: 600 }}> Form 1120-S </strong>{' '}
                    is filed by S corporations with{' '}
                    <strong style={{ fontWeight: 600 }}> IRS</strong> to report
                    income, deductions, credits, and other tax items. Income
                    generally passes through to shareholders and is reported on
                    <strong style={{ fontWeight: 600 }}> Schedule K-1</strong>.
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <Link
                          href="/resources/business-tax/form-1120-s-filing-deadlines-and-penalties-guide/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Due Date
                        </Link>
                        : Calendar-year S corporations must file by{' '}
                        <strong style={{ fontWeight: 600 }}> March 15</strong>.
                        Fiscal-year S corporations generally file by the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          15th day of the 3rd month
                        </strong>{' '}
                        after the end of their tax year.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}> Extension:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          File Form 7004
                        </Link>{' '}
                        by the original due date to receive a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          6-month automatic extension
                        </strong>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Late Filing Penalties:
                        </strong>{' '}
                        The IRS may charge{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $245 per shareholder per month
                        </strong>{' '}
                        for late filing, plus penalties for{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          missing or incorrect Schedule K-1s
                        </strong>
                        . If filed more than{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          60 days late
                        </strong>
                        , a
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          minimum penalty of $525
                        </strong>{' '}
                        may apply.
                      </span>
                    </li>
                  </Box>
                </AtAGlance>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you run an{' '}
                  <strong style={{ fontWeight: 600 }}>S Corporation</strong>,
                  IRS 1120s tax form is your most important annual tax filing.
                  Unlike a traditional C Corporation (which files tax Form 1120
                  and pays corporate income taxes), an S Corporation operates as
                  a <span style={{ fontWeight: 600 }}>pass-through entity</span>
                  . This means the corporation itself doesn’t pay federal income
                  tax - instead, the income, deductions, and credits “flow
                  through” to shareholders, who report them on their personal
                  tax returns.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  That’s why filing IRS Form 1120-S is critical. It doesn’t just
                  satisfy IRS compliance; it ensures your shareholders can file
                  their personal returns accurately using{' '}
                  <span style={{ fontWeight: 600 }}>Schedule K-1</span>.
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> If you’re searching for IRS Form 1120-S
                  instructions or how to file 1120-S online, this guide will
                  take you step by step through the form with examples, FAQs,
                  and expert insights.
                </Alert>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatnew" sx={{ color: '#0202ff' }}>
                          What’s New for Form 1120-S (Tax Year 2025)
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What Is Form 1120-S and Why It Matters
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who Must File Form 1120-S?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#filing" sx={{ color: '#0202ff' }}>
                          Filing Deadlines and Penalties
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key Documents You Need Before Filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#line" sx={{ color: '#0202ff' }}>
                          Line-by-Line Instructions below
                        </Link>
                        <List sx={{ pb: 0 }}>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#basic" sx={{ color: '#0202ff' }}>
                                Basic Information Section
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#income" sx={{ color: '#0202ff' }}>
                                Income Section
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#section" sx={{ color: '#0202ff' }}>
                                Deductions Section
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#tax" sx={{ color: '#0202ff' }}>
                                Tax and Payments Section
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#schb" sx={{ color: '#0202ff' }}>
                                Schedule B – Other Information (overview)
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#schk" sx={{ color: '#0202ff' }}>
                                Schedule K
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#schl" sx={{ color: '#0202ff' }}>
                                Schedule L
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#schm1" sx={{ color: '#0202ff' }}>
                                Schedule M-1
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#schm2" sx={{ color: '#0202ff' }}>
                                Schedule M-2
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Mistakes to Avoid
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why File Form 1120-S Online with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#faq" sx={{ color: '#0202ff' }}>
                          Frequently Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whatnew"
                >
                  What’s New for Form 1120-S (Tax Year 2025)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Several important IRS updates apply to S Corporations filing
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 1120-S for tax year 2025 (filed in 2026).
                  </span>{' '}
                  These changes affect penalties, refunds, payments, and how
                  certain business expenses are treated.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ fontSize: { md: 20, sm: 18, xs: 16 }, fontWeight: 600 }}
                >
                  Higher Late-Filing Penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Form 1120 s corporation is filed{' '}
                  <span style={{ fontWeight: 600 }}>
                    more than 60 days late,
                  </span>{' '}
                  the <span style={{ fontWeight: 600 }}>minimum penalty</span>{' '}
                  is now:{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  The smaller of the tax due or $525{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This applies even if the corporation owes little or no tax.
                  Late-filing penalties are{' '}
                  <span style={{ fontWeight: 600 }}>in addition</span> to the
                  per-shareholder penalties for missing or late Schedule K-1s.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whatis"
                >
                  What Is Form 1120-S and Why It Matters
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 1120-S,{' '}
                  <span style={{ fontStyle: 'italic' }}>
                    {' '}
                    U.S. Income Tax Return for an S Corporation
                  </span>
                  , is the IRS form used to report:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>The corporation’s income and losses </li>
                  <li>Deductions and credits </li>
                  <li>Balance sheet details </li>
                  <li>Shareholder distributions </li>
                  <li>Supporting schedules (K, K-1, L, M-1, and M-2) </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Why it matters:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Compliance –</span>{' '}
                    Failure to file can result in penalties.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Transparency –</span>{' '}
                    Shareholders rely on timely K-1s.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Accuracy –</span> Mistakes
                    can cause delays in both corporate and personal
                    filings.{' '}
                  </li>
                </Box>
                <Alert severity="info" icon={false} sx={{ mb: 4 }}>
                  <b>Pro Tip:</b> Even if your S Corp had{' '}
                  <span style={{ fontWeight: 600 }}>no income</span> for the
                  year, you may still be required to file Form 1120-S
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who Must File Form 1120-S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  You must file Form 1120s if your business:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Has elected to be treated as an{' '}
                    <span style={{ fontWeight: 600 }}>S Corporation</span> by
                    filing Form 2553 (Election by a Small Business Corporation).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Operates as a domestic corporation with{' '}
                    <span style={{ fontWeight: 600 }}>
                      100 or fewer shareholders
                    </span>
                    .
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Meets shareholder eligibility rules (only U.S. citizens,
                    residents, and certain trusts/estates can hold shares).{' '}
                  </li>
                  <li>
                    Has issued{' '}
                    <span style={{ fontWeight: 600 }}>Schedule K-1</span> to
                    shareholders.{' '}
                  </li>
                </ul>
                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Important :</b> If your s-corporation status was terminated
                  (for example, by exceeding 100 shareholders or having an
                  ineligible shareholder), you cannot file 1120-s form. Instead,
                  you must file{' '}
                  <Link
                    href="https://www.irs.gov/forms-pubs/about-form-1120"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1120
                  </Link>{' '}
                  as a C Corporation.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="filing"
                >
                  Filing Deadlines and Penalties
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Form 1120 s Due Date:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      Calendar-year S Corporations
                    </span>{' '}
                    (most common): File by
                    <span style={{ fontWeight: 600 }}> March 16, 2026</span> for
                    the 2025 tax year.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      Fiscal-year S Corporations:
                    </span>{' '}
                    File by the{' '}
                    <span style={{ fontWeight: 600 }}>15th day</span> of the{' '}
                    <span style={{ fontWeight: 600 }}>3rd month</span> after
                    your fiscal year ends.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Extensions
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    You can file{' '}
                    <Link
                      href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 7004
                    </Link>{' '}
                    to request a 6-month extension.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Example: For a calendar-year S Corp, the extended deadline
                    is{' '}
                    <span style={{ fontWeight: 600 }}>September 15, 2026</span>.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Penalties
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Situation
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Penalty
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Late filing </TableCell>
                          <TableCell>
                            $245 per shareholder per month (up to 12 months)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Missing or incorrect K-1 </TableCell>
                          <TableCell>$310 per K-1 </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Intentional disregard </TableCell>
                          <TableCell>$630+ per K-1 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Filed more than 60 days late{' '}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Minimum $525 or tax due (whichever is lower){' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 2, mb: 2 }}
                >
                  <b>Pro Tip:</b> Filing even a{' '}
                  <span style={{ fontWeight: 600 }}>zero-income 1120-S</span>{' '}
                  avoids these penalties.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Comparison Table (1120-S vs. 1120 vs. 1065)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Instead of repeating what’s in your Resource Center, make it
                  <span style={{ fontWeight: 600 }}> instruction-focused:</span>
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            Form
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            Who Files
                          </TableCell>
                          <TableCell
                            sx={{ color: '#444', width: 400, fontWeight: 600 }}
                          >
                            Key Difference
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-1120-s"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              1120-S{' '}
                            </Link>{' '}
                          </TableCell>
                          <TableCell>S Corps</TableCell>

                          <TableCell>
                            Pass-through taxation, must send K-1s{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-1120"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              1120{' '}
                            </Link>{' '}
                          </TableCell>
                          <TableCell>C Corps </TableCell>

                          <TableCell>
                            Pays corporate tax at entity level{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-1065"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              1065
                            </Link>{' '}
                          </TableCell>
                          <TableCell>Partnerships </TableCell>

                          <TableCell>
                            Pass-through taxation, but no corporate
                            structure{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 2, mb: 2 }}
                >
                  <b>Pro Tip:</b> E-filing through an IRS-authorized provider
                  like <span style={{ fontWeight: 600 }}>TaxZerone</span>{' '}
                  reduces the risk of missed deadlines and errors.
                </Alert>
                {/* <Box
                  component={'div'}
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0, lineHeight: 1.3 }}
                      >
                        Submit Your BOI Report Now with TaxZerone
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 130, textAlign: 'center' }}
                      id="common"
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boircompany`}
                      >
                        E-file Now!
                      </Button>
                    </Box>
                  </Stack>
                </Box> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="key"
                >
                  Key Documents You Need Before Filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Before filling out Form 1120-S, gather:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Year-end financial statements
                    </span>{' '}
                    (Profit & Loss, Balance Sheet)
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Shareholder ownership records
                    </span>{' '}
                    (percentages, contributions, distributions)
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Supporting schedules
                    </span>{' '}
                    (depreciation schedules, COGS details, credit documentation)
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Last year’s 1120-S</span>{' '}
                    (for consistency and carryovers)
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Payroll records</span> (if
                    you pay shareholder-employees)
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Key Schedules for IRS Form 1120-S
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Filing 1120 s form involves several required schedules and
                  supporting attachments. Here’s a breakdown of every major
                  schedule, what it records, and when you need it:
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: 150,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Purpose
                          </TableCell>
                          <TableCell sx={{ width: 200, fontWeight: 600 }}>
                            Required When
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule B-1
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Information on indirect/nominee shareholders
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Any indirect/nominee ownership
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule D
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Capital gains and losses
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Any sale/exchange of capital assets
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule K
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            S Corp’s total annual income, deductions, and
                            credits
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            All S Corporations
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule K-1
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Individual shareholder allocations
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Every shareholder
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule L
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Year-end balance sheet
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Gross receipts/assets exceed IRS thresholds
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule M-1
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Book-tax reconciliation
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Assets/receipts &gt; $250,000
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule M-2
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            AAA balance/distributions analysis
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            S Corps distributing to shareholders
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule K-2/K-3
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            International tax compliance items (where relevant)
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Foreign activity/cross-border tax items
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1125-A
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Cost of Goods Sold computation
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Product-based businesses
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            4562
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Depreciation and amortization
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Assets depreciated/amortized
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            4797
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Sales of business property
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Asset/property sales
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Additional Attachments
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Rental income, vehicle credits, opportunity funds,
                            and others as applicable
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Specific events or transactions
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
                  Each schedule should be prepared and attached in the precise
                  order recommended by the IRS to avoid processing delays and
                  audit risk.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  How These Schedules Fit
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Schedules B, K, L, M-1, and M-2 are integral parts of the
                    main Form 1120-S and assembled at the end.{' '}
                  </li>
                  <li>
                    Schedules K-1 are distributed to shareholders for personal
                    filings.{' '}
                  </li>
                  <li>
                    Attach other forms (
                    <span style={{ fontWeight: 600 }}>
                      D, 1125-A, 4562, 4797, K-2/K-3, etc.
                    </span>
                    ) if specific business activity applies.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="line"
                >
                  Line-by-Line Instructions for Form 1120-S
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Let’s now explore the line-by-line instructions for form 1120
                  s. This section provides a detailed summary of your
                  corporation’s financial activity and tax reporting.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                  id="basic"
                >
                  Basic Information Section
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-basic-info-img.webp" />
                  <source srcSet="/assets/images/1120s-basic-info-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-basic-info-img.png"
                    loading="lazy"
                    alt="Basic Information Section (Top of the Form)"
                    width={757}
                    height={179}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Name and Address</span> –
                    Enter the exact legal name of your corporation as it appears
                    on incorporation documents. Include full mailing address.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Employer Identification Number (EIN)
                    </span>{' '}
                    – This is your corporate tax ID, not a personal SSN.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Date Incorporated</span> –
                    Enter the month, day, and year of incorporation.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Total Assets </span> –
                    Enter the corporation’s total assets at year-end (from your
                    balance sheet, Schedule L).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Final Return / Name Change / Address Change{' '}
                    </span>{' '}
                    – Check these boxes if applicable.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Example:</span> If your S
                  Corp closed in 2025, check “Final Return.”
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="income"
                >
                  Income Section (Lines 1a–6)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-income-sec-img.webp" />
                  <source srcSet="/assets/images/1120s-income-sec-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-income-sec-img.png"
                    loading="lazy"
                    alt="Income Section (Lines 1a–6)"
                    width={759}
                    height={103}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This section reports your S Corporation’s income from business
                  operations and other sources for the tax year. It begins with
                  total gross sales and subtracts returns and allowances to
                  calculate net sales. Then, if applicable, it accounts for the
                  cost of goods sold (COGS) to determine gross profit. Gains or
                  losses from selling business property and other income like
                  rental or interest income are added to calculate total income.
                  Accurate reporting here is essential to reflect your
                  corporation’s financial activity correctly.
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 1a – Gross Receipts or Sales
                    </span>{' '}
                    <br />
                    Enter your total sales revenue before deducting returns or
                    allowances.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    $500,000 in gross sales.
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 1b – Returns and Allowances
                    </span>{' '}
                    <br /> Report refunds, rebates, or returned goods.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      Example:
                    </span>{' '}
                    $5,000 in product returns.
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 1c – Balance (Net Sales)
                    </span>{' '}
                    <br />
                    Subtract Line 1b from Line 1a to get net sales.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      Example:
                    </span>{' '}
                    ($500,000 – $5,000 = $495,000).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 2 – Cost of Goods Sold (COGS){' '}
                    </span>{' '}
                    <br /> Enter the total cost of producing or acquiring the
                    goods sold. Attach Form 1125-A to detail this amount.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    $200,000 in materials and inventory costs.{' '}
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 3 – Gross Profit
                    </span>{' '}
                    <br /> Subtract Line 2 from Line 1c to calculate gross
                    profit.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    $495,000 – $200,000 = $295,000.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 4 – Net Gain (Loss) from Form 4797
                    </span>{' '}
                    <br /> Enter any gains or losses from the sale of business
                    property as reported on Form 4797, Part II, Line 17. Attach
                    Form 4797. <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    Gain of $15,000 from sale of machinery.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 5 – Other Income (Loss)
                    </span>{' '}
                    <br /> Report other income or loss, such as rental income,
                    interest income, or tax refunds. Attach statements as needed
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      Example:
                    </span>{' '}
                    $3,000 in interest income.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 6 – Total Income (Loss)
                    </span>{' '}
                    <br />
                    Add Lines 3, 4, and 5 to get total income or loss.
                    <br />{' '}
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      Example:
                    </span>{' '}
                    $295,000 + $15,000 + $3,000 = $313,000.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="section"
                >
                  Deductions Section Overview (Lines 7–22)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section lists your S Corporation’s allowable business
                  expenses that reduce taxable income. Each deduction must be
                  ordinary, necessary, and well-documented. Properly reporting
                  deductions ensures accurate calculation of your corporation’s
                  ordinary business income or loss, which flows through to
                  shareholders.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1.5,
                  }}
                >
                  Line-by-Line Instructions{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-line-img.webp" />
                  <source srcSet="/assets/images/1120s-line-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-line-img.png"
                    loading="lazy"
                    alt="Line-by-Line Instructions"
                    width={771}
                    height={276}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 7 – Compensation of Officers
                    </span>{' '}
                    <br />
                    Report salaries paid to shareholder-officers actively
                    working in the business. IRS requires this compensation to
                    be reasonable based on industry standards.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    The CEO’s salary is $80,000.
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 8 – Salaries and Wages
                    </span>{' '}
                    <br />
                    Include wages paid to non-officer employees; do not include
                    officer compensation here.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    Administrative staff wages total $150,000.
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 9 – Repairs and Maintenance
                    </span>{' '}
                    <br />
                    Expenses to keep business property in good working order
                    (not capital improvements).
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    Repainting office space or fixing machinery.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 10 – Bad Debts{' '}
                    </span>{' '}
                    <br /> Deduct accounts receivables that are uncollectible
                    and were previously included in income.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    A $5,000 client invoice that cannot be collected.
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>Line 11 – Rents</span>{' '}
                    <br /> Rent paid for business property or equipment.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    Office lease payments totaling $24,000 annually.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 12 – Taxes and Licenses
                    </span>{' '}
                    <br /> Includes state and local business taxes, payroll
                    taxes, and licenses but excludes federal income taxes.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    State franchise tax of $10,000.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 13 – Interest</span>{' '}
                    <br /> Interest on loans or business mortgages.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    $8,000 paid on a business loan interest.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 14 – Depreciation
                    </span>{' '}
                    <br />
                    Report depreciation expense recorded on{' '}
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-4562"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 4562
                    </Link>
                    .
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    Depreciation for equipment is $5,000 for the year.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Line 15 – Depletion</span>{' '}
                    <br />
                    For businesses extracting natural resources like mining or
                    oil.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    Depletion expense related to oil extraction.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 16 – Advertising
                    </span>{' '}
                    <br />
                    Marketing and promotional expenses.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    $20,000 spent on online ads and brochures.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 17 – Pension, Profit-Sharing, etc.
                    </span>{' '}
                    <br />
                    Contributions to employee retirement plans.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    $12,000 paid into a SEP IRA plan.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 18 – Employee Benefit Programs
                    </span>{' '}
                    <br />
                    Costs for employee health insurance, life insurance, and
                    other benefits (excluding pension).
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    Company-paid health insurance premiums of $18,000.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 19 – Energy Efficient Commercial Buildings Deduction
                    </span>{' '}
                    <br />
                    Deduct expenses for qualifying energy-efficient improvements
                    (attach Form 7205).
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    Costs associated with installing energy-efficient windows.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 20 – Total Deductions
                    </span>{' '}
                    <br />
                    List any other ordinary business expenses not listed above
                    (attach itemized statement).
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      {' '}
                      Example:
                    </span>{' '}
                    Legal fees, office supplies, utilities.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 21 – Total Deductions
                    </span>{' '}
                    <br />
                    Add Lines 7 through 20 to report the total deductible
                    expenses.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 22 – Ordinary Business Income (Loss)
                    </span>{' '}
                    <br />
                    Subtract Line 21 (Total Deductions) from Line 6 (Total
                    Income) to calculate your ordinary business income or loss.
                    This amount flows to{' '}
                    <span style={{ fontWeight: 600 }}>Schedule K</span> and
                    ultimately to shareholders.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Example Summary:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Total Income (Line 6) = $295,000 </li>
                  <li>Total Deductions (Line 21) = $260,000 </li>
                  <li>Ordinary Business Income (Line 22) = $35,000 </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Tax and Payments Section
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Understanding the Tax and Payments Section (Lines 23a–28)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section of Form 1120-S tax form reports any
                  corporate-level taxes owed by your S Corporation, estimated
                  tax payments made during the year, penalties (if applicable),
                  and the calculation of either the amount you owe or your
                  overpayment for the tax year.{' '}
                </Typography>
                <Typography variant="body1">
                  Because S Corporations generally do not pay income tax at the
                  corporate level, these lines specifically address:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Taxes on excess passive income or asset recapture,</li>
                  <li>Capital gains taxes, </li>
                  <li>Tax credits and prepayments, </li>
                  <li>Any penalties for underpayment of estimated taxes. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Filing this section correctly ensures accurate tax liability
                  calculation and avoids unexpected penalties or missed refunds.
                  Below, each line is explained with examples to help you
                  complete this important part of your return confidently.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23a – Excess Net Passive Income or LIFO Recapture Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  <span style={{ fontWeight: 600, fontStyle: 'initial' }}>
                    {' '}
                    Example:
                  </span>{' '}
                  If your S corporation had excess net passive income of $4,000
                  and must pay a recapture tax of $500, enter the total, $4,500,
                  here. Attach a statement calculating each tax.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23b – Tax from Schedule D (Form 1120-S)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  <span style={{ fontWeight: 600, fontStyle: 'initial' }}>
                    {' '}
                    Example:
                  </span>{' '}
                  After selling business assets, you calculate from Schedule D
                  that your S corporation owes $2,000 in capital gains tax.
                  Enter $2,000.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23c – Total Taxes (Add Lines 23a and 23b plus additional
                  taxes, if any)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  <span style={{ fontWeight: 600, fontStyle: 'initial' }}>
                    {' '}
                    Example:
                  </span>{' '}
                  If Line 23a is $4,500, Line 23b is $2,000, and you owe $700
                  from Form 4255 (investment credit recapture), enter $7,200.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 24a – Estimated Tax Payments & Prior Year Overpayments
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  <span style={{ fontWeight: 600, fontStyle: 'initial' }}>
                    {' '}
                    Example:
                  </span>{' '}
                  During the year, your S corporation made $3,000 in estimated
                  payments and carried forward a $500 overpayment from last
                  year. Enter $3,500.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 24b – Tax Deposited with Form 7004
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  <span style={{ fontWeight: 600, fontStyle: 'initial' }}>
                    {' '}
                    Example:
                  </span>{' '}
                  When filing for an extension, your S corporation prepaid
                  $1,000 with Form 7004. Enter $1,000.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 24c – Credit for Federal Tax Paid on Fuels (Form 4136)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  <span style={{ fontWeight: 600, fontStyle: 'initial' }}>
                    {' '}
                    Example:
                  </span>{' '}
                  If your S corporation qualifies for a $250 fuel tax credit,
                  based on Form 4136, enter $250.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 24d – Elective Payment Election Amount (Form 3800)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  <span style={{ fontWeight: 600, fontStyle: 'initial' }}>
                    {' '}
                    Example:
                  </span>{' '}
                  Your S corp claims a $1,200 elective payment for renewable
                  energy credits, calculated on Form 3800. Enter $1,200.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 24z – Total Prepaid Taxes and Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  <span style={{ fontWeight: 600, fontStyle: 'initial' }}>
                    {' '}
                    Example:
                  </span>{' '}
                  Adding Lines 24a ($3,500), 24b ($1,000), 24c ($250), and 24d
                  ($1,200) totals $5,950. Enter $5,950.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25 – Estimated Tax Penalty (if any; Attach Form 2220 if
                  used)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  <span style={{ fontWeight: 600, fontStyle: 'initial' }}>
                    {' '}
                    Example:
                  </span>{' '}
                  Your S corporation underpaid estimated taxes and owes a $90
                  penalty. Enter $90. If using Form 2220, check the box.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 26 – Amount Owed
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  <span style={{ fontWeight: 600, fontStyle: 'initial' }}>
                    {' '}
                    Example:
                  </span>{' '}
                  If total taxes due (Line 23c + 25) are $7,290 and total
                  prepayments (Line 24z) are $5,950, enter $1,340 ($7,290 –
                  $5,950).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 27 – Overpayment
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  <span style={{ fontWeight: 600, fontStyle: 'initial' }}>
                    {' '}
                    Example:
                  </span>{' '}
                  If total prepayments (Line 24z) are higher than total taxes
                  (Line 23c + 25), say by $500, enter $500 here.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28 – Amount Credited to Estimated Tax or Refunded
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  If the S corporation has an overpayment shown on Line 27, it
                  must decide how that amount should be handled. The corporation
                  can either apply the overpayment to the next year’s estimated
                  tax or request that the IRS refund the amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  Line 28 is used to indicate the corporation’s choice and, if a
                  refund is requested, to provide banking details for direct
                  deposit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28a – Credited to Next Year’s Estimated Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  Enter the portion of the overpayment from Line 27 that the
                  corporation wants to apply toward its estimated tax liability
                  for the following tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  Applying the overpayment to next year’s estimated taxes may
                  reduce or offset upcoming estimated tax payments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  Once the corporation elects to apply the overpayment to the
                  next year’s estimated tax, this decision cannot be reversed
                  later.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28b – Refunded
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the amount of the overpayment the corporation wants to
                  receive as a refund.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  If the corporation is eligible and has access to U.S. banking
                  services, it is recommended to request the refund through
                  direct deposit, which is generally faster and more secure than
                  receiving a paper check.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  If the corporation wants the refund deposited directly into
                  its bank account, it must complete Lines 28c through 28e.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  However, direct deposit is not available if:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  The receiving bank is a foreign financial institution or
                  foreign branch of a U.S. bank, or The corporation has applied
                  for an EIN but has not yet received it at the time the return
                  is filed.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28c – Routing Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  Enter the nine-digit routing number for the corporation’s bank
                  or financial institution.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  The routing number identifies the bank where the refund will
                  be deposited. If the routing number shown on deposit slips
                  differs from the one on checks, confirm the correct number
                  with the financial institution before entering it on the form.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28d – Type of Account
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  Check the box that corresponds to the type of bank account
                  where the refund should be deposited.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  Available options include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Checking</li>
                  <li>Savings </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2.5 }}>
                  Only one box should be selected to ensure the deposit is
                  processed correctly.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28e – Account Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  Enter the corporation’s bank account number where the refund
                  should be deposited.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  The account number may contain up to 17 characters, including
                  numbers or letters. Hyphens may be included if they are part
                  of the account number, but spaces or other special symbols
                  should not be used. Enter the account number from left to
                  right, leaving any unused boxes empty.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  Do not include the check number in this field.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Additional Note: </b> If the amount deposited into the
                  corporation’s account differs from the expected refund, the
                  IRS will typically send an explanation notice by mail within
                  approximately two weeks after the deposit is made.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schb"
                >
                  Schedule B – Other Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Schedule B collects additional details about the S Corporation
                  that are important for IRS reporting but don’t fit elsewhere
                  on the form. It consists mainly of yes/no questions and
                  requests for specific entity and ownership information to help
                  the IRS understand your corporation’s structure, activities,
                  and compliance with tax rules.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Key items covered:{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-schb-img.webp" />
                  <source srcSet="/assets/images/1120s-schb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-schb-img.png"
                    loading="lazy"
                    alt="Schedule B – Other Information"
                    width={760}
                    height={473}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Accounting Method</span>
                    <br />
                    Indicate whether your corporation uses the cash method,
                    accrual method, or another accounting method.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Business Activity and Product/Service
                    </span>
                    <br />
                    Enter your primary business activity and describe the main
                    products or services offered.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Shareholder Entities
                    </span>{' '}
                    <br />
                    Disclose if any shareholder during the tax year was a
                    disregarded entity (e.g., <br />
                    single-member LLC), a trust, estate, nominee, or similar
                    person. If yes, you must attach Schedule B-1 providing
                    detailed information.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Ownership of Other Entities
                    </span>{' '}
                    <br />
                    Report whether your S Corporation directly or indirectly
                    owns 20% or more of any other corporation’s stock or has
                    significant interests (20% or more) in partnerships or
                    trusts. Detailed ownership information and related entities
                    must be provided.
                  </li>
                  <picture>
                    <source srcSet="/assets/images/1120s-stock-img.webp" />
                    <source srcSet="/assets/images/1120s-stock-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/1120s-stock-img.png"
                      loading="lazy"
                      alt="Schedule B – Other Information"
                      width={770}
                      height={499}
                      className="img-responsive"
                      sx={{
                        border: '2px solid #333',
                        maxWidth: '100%',
                        mb: 0,
                      }}
                    />
                  </picture>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Restricted Stock and Stock Options
                    </span>{' '}
                    <br />
                    Disclose any outstanding shares of restricted stock or stock
                    options/warrants at year-end with relevant totals.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Material Advisor Disclosure
                    </span>{' '}
                    <br />
                    Indicate whether you are required to file Form 8918 related
                    to reportable transactions.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Publicly Offered Debt Instruments
                    </span>{' '}
                    <br />
                    If you issued publicly offered debt instruments with
                    original issue discount, disclose here, which may require
                    filing Form 8281.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Net Unrealized Built-in Gain
                    </span>{' '}
                    <br />
                    Report any built-in gain related to previous C corporation
                    status or asset acquisitions from a C corporation.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Section 163(j) Election
                    </span>{' '}
                    <br />
                    Confirm if your corporation had an election under Section
                    163(j) related to limitations on business interest expense
                    for real property or farming businesses.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Business Interest Expense Limitation
                    </span>{' '}
                    <br />
                    Indicate if your corporation meets criteria requiring Form
                    8990 related to business interest expense limitations.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Small Corporation Exception
                    </span>{' '}
                    <br />
                    State if total receipts and total assets are below $250,000,
                    which exempts you from filing Schedules L and M-1.
                  </li>
                  <picture>
                    <source srcSet="/assets/images/1120s-canceled-img.webp" />
                    <source srcSet="/assets/images/1120s-canceled-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/1120s-canceled-img.png"
                      loading="lazy"
                      alt="Schedule B – Other Information"
                      width={760}
                      height={187}
                      className="img-responsive"
                      sx={{
                        border: '2px solid #333',
                        maxWidth: '100%',
                        mb: 0,
                      }}
                    />
                  </picture>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Canceled or Modified Debt
                    </span>{' '}
                    <br />
                    Report any non-shareholder debt canceled, forgiven, or
                    modified during the year.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Qualified Subchapter S Subsidiary Election
                    </span>{' '}
                    <br />
                    Disclose if any such election was terminated or revoked
                    during the year.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Payments Requiring Form 1099
                    </span>{' '}
                    <br />
                    Indicate whether you made payments necessitating Form(s)
                    1099 filings and whether these were filed.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Qualified Opportunity Fund Certification
                    </span>{' '}
                    <br />
                    <Alert severity="warning" icon={false} sx={{ mb: 3 }}>
                      <b> Note:</b> If you are attaching Form 8996 certifying as
                      a Qualified Opportunity Fund.
                    </Alert>
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Digital Asset Transactions
                    </span>{' '}
                    <br />
                    Indicate if your corporation received or disposed of any
                    digital assets (such as cryptocurrencies) during the tax
                    year.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Additional Information:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If you answered “Yes” to having shareholders who are
                    disregarded entities or similar, attach Schedule B-1 to
                    provide their details.{' '}
                  </li>
                  <li>
                    Attach supporting statements or forms as required for each
                    respective item.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                  id="schk"
                >
                  Schedule K – Shareholders’ Pro Rata Share Items
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-schk-img.webp" />
                  <source srcSet="/assets/images/1120s-schk-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-schk-img.png"
                    loading="lazy"
                    alt="Schedule K – Shareholders’ Pro Rata Share Items"
                    width={752}
                    height={263}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule K of Form 1120-S summarizes all income, deductions,
                  credits, and other tax items that are allocated to
                  shareholders proportionate to their ownership shares. These
                  amounts flow through to shareholders for reporting on their
                  personal tax returns via Schedule K-1.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Income (Loss) Items
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Ordinary Business Income (Loss)
                    </span>
                    <br />
                    The corporation’s net ordinary income or loss from
                    operations (Page 1, Line 22).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Net Rental Real Estate Income (Loss)
                    </span>
                    <br />
                    Income or loss from rental real estate activities (attach
                    Form 8825 if applicable).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Other Gross Rental Income (Loss) and Expenses
                    </span>
                    <br />
                    Report income and expenses from rental activities not
                    involving real estate. Subtract expenses (3b) from gross
                    rental income (3a) for net rental income (3c).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Interest Income</span>
                    <br />
                    Report interest earned from business investments and bank
                    accounts.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Dividends</span>
                  </li>
                  <ul style={{ listStyleType: 'lower-alpha' }}>
                    <li>Ordinary Dividends.</li>
                    <li>
                      Qualified Dividends (subject to preferential tax
                      rates).{' '}
                    </li>
                  </ul>
                  <li>
                    <span style={{ fontWeight: 600 }}>Royalties</span>
                    <br />
                    Income received from intellectual property or natural
                    resource rights.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Net Short-Term Capital Gain (Loss)
                    </span>
                    <br />
                    Gains or losses from assets held one year or less (attach
                    Schedule D).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Net Long-Term Capital Gain (Loss) and Related Gains
                    </span>
                  </li>
                  <ul style={{ listStyleType: 'lower-alpha' }}>
                    <li>
                      {' '}
                      Long-term gains or losses (held more than one year)
                    </li>
                    <li>Collectibles (28%) gains or losses</li>
                    <li>
                      Unrecaptured section 1250 gains (attach statement).{' '}
                    </li>
                  </ul>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Net Section 1231 Gain (Loss)
                    </span>
                    <br />
                    Gains or losses from sales of business property (attach Form
                    4797).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Other Income (Loss)</span>
                    <br />
                    Miscellaneous income or loss not included above (specify
                    type).
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Deductions{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-deduction-img.webp" />
                  <source srcSet="/assets/images/1120s-deduction-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-deduction-img.png"
                    loading="lazy"
                    alt="Deductions"
                    width={751}
                    height={102}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box
                  component="ol"
                  start={11}
                  sx={{ mb: 3, '& li': { mb: 1.2 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Section 179 Deduction
                    </span>
                    <br />
                    Expensing election for qualifying property (attach Form
                    4562).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Charitable Contributions and Investment Interest Expense
                    </span>
                  </li>
                  <ul style={{ listStyleType: 'lower-alpha' }}>
                    <li>Cash charitable contributions</li>
                    <li> Noncash charitable contributions</li>
                    <li> Investment interest expense</li>
                    <li>Section 59(e)(2) expenditures</li>
                    <li>Other deductions (specify).</li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Credits
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-credits-img.webp" />
                  <source srcSet="/assets/images/1120s-credits-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-credits-img.png"
                    loading="lazy"
                    alt="Credits"
                    width={757}
                    height={119}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Box
                  component="ol"
                  start={13}
                  sx={{ mb: 3, '& li': { mb: 1.2 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Various tax credits such as:
                    </span>
                  </li>
                  <ul style={{ listStyleType: 'lower-alpha' }}>
                    <li> Low-income housing credits</li>
                    <li> Qualified rehabilitation expenditures</li>
                    <li> Other rental real estate credits</li>
                    <li>Biofuel producer credit</li>
                    <li>Other applicable credits (specify)</li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  International Tax Items
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-international-img.webp" />
                  <source srcSet="/assets/images/1120s-international-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-international-img.png"
                    loading="lazy"
                    alt="International Tax Items"
                    width={747}
                    height={52}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box
                  component="ol"
                  start={14}
                  sx={{ mb: 3, '& li': { mb: 1.2 } }}
                >
                  <li>
                    Attach Schedule K-2 if reporting international tax items and
                    check the appropriate box.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Alternative Minimum Tax (AMT) Items
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-alternative-min-img.webp" />
                  <source srcSet="/assets/images/1120s-alternative-min-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-alternative-min-img.png"
                    loading="lazy"
                    alt="Alternative Minimum Tax (AMT) Items"
                    width={759}
                    height={103}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Box
                  component="ol"
                  start={15}
                  sx={{ mb: 3, '& li': { mb: 1.2 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Adjustments related to AMT such as:
                    </span>
                  </li>
                  <ul style={{ listStyleType: 'lower-alpha' }}>
                    <li>Post-1986 depreciation adjustments </li>
                    <li>Adjusted gain or loss</li>
                    <li>Depletion</li>
                    <li>
                      Oil, gas, and geothermal properties income and deductions
                    </li>
                    <li>Other AMT-related items.</li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Items Affecting Shareholder Basis
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-items-affect-img.webp" />
                  <source srcSet="/assets/images/1120s-items-affect-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-items-affect-img.png"
                    loading="lazy"
                    alt="Items Affecting Shareholder Basis"
                    width={751}
                    height={102}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box
                  component="ol"
                  start={16}
                  sx={{ mb: 3, '& li': { mb: 1.2 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>Items like:</span>
                  </li>
                  <ul style={{ listStyleType: 'lower-alpha' }}>
                    <li>Tax-exempt interest income</li>
                    <li>Other tax-exempt income</li>
                    <li>Nondeductible expenses</li>
                    <li>Distributions to shareholders</li>
                    <li> Repayments of loans from shareholders</li>
                    <li>Foreign taxes paid or accrued</li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Other Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-other-info-img.webp" />
                  <source srcSet="/assets/images/1120s-other-info-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-other-info-img.png"
                    loading="lazy"
                    alt="Other Information"
                    width={752}
                    height={69}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box
                  component="ol"
                  start={17}
                  sx={{ mb: 3, '& li': { mb: 1.2 } }}
                >
                  <li>
                    Investment income and expenses, dividend distributions, and
                    other items.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Income (Loss) Reconciliation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-reconciliation-img.webp" />
                  <source srcSet="/assets/images/1120s-reconciliation-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-reconciliation-img.png"
                    loading="lazy"
                    alt="Income (Loss) Reconciliation"
                    width={753}
                    height={51}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Box component="ol" start={18} sx={{ mb: 2 }}>
                  <li>
                    {' '}
                    Combine total income amounts (Lines 1–10), then subtract
                    deductions (Lines 11–12e) and foreign taxes (Line 16f) to
                    reconcile the corporation’s income or loss.
                  </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  This schedule provides shareholders with a complete view of
                  their share of the S Corporation’s financial activities
                  necessary for their individual income tax filings.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schk"
                >
                  Schedule K-1 – Shareholder Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Each shareholder receives a{' '}
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/f1120ssk.pdf"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Schedule K-1
                  </Link>{' '}
                  reporting their share of income, losses, deductions, and
                  credits.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What K-1 Includes
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Shareholder’s identifying info (name, SSN, address). </li>
                  <li>Ownership percentage (beginning and end of year). </li>
                  <li>Allocated share of Schedule K items. </li>
                  <li>Foreign transactions, AMT items, and other credits. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Example:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>2 shareholders, each with 50% ownership. </li>
                  <li>Ordinary Business Income = $35,000. </li>
                  <li>Each K-1 shows $17,500 for Line 1. </li>
                </Box>
                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 2, mb: 4 }}
                >
                  <b>Pro Tip:</b> Late or inaccurate K-1s can trigger IRS
                  penalties. Always distribute them{' '}
                  <span style={{ fontWeight: 600 }}>
                    immediately after filing Form 1120-S
                  </span>
                  .
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schl"
                >
                  Schedule L – Balance Sheets per Books
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule L provides a snapshot of your S Corporation’s
                  financial position at the beginning and end of the tax year.
                  It reports the corporation’s assets, liabilities, and
                  shareholders’ equity as recorded in its books and records.
                  This balance sheet helps the IRS verify income and deductions
                  reported elsewhere on the tax return.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Assets Section{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-schl-img.webp" />
                  <source srcSet="/assets/images/1120s-schl-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-schl-img.png"
                    loading="lazy"
                    alt="Assets Section"
                    width={748}
                    height={350}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report amounts for the following asset categories at both the
                  beginning and end of the tax year:{' '}
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>Cash:</span> All cash on
                    hand and in bank accounts.
                  </li>
                  <li>
                    <ul style={{ listStyleType: 'lower-alpha' }}>
                      <li>
                        {' '}
                        <span style={{ fontWeight: 600 }}>
                          Trade Notes and Accounts Receivable:
                        </span>{' '}
                        Amounts owed to the corporation by customers.
                      </li>
                      <li>
                        <span style={{ fontWeight: 600 }}>
                          Allowance for Bad Debts:
                        </span>{' '}
                        Subtract estimated uncollectible amounts from
                        receivables.{' '}
                      </li>
                    </ul>
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>Inventories:</span> Goods
                    available for sale (see Form 1125-A for details).
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      U.S. Government Obligations:
                    </span>{' '}
                    Treasury bonds and similar securities.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Tax-Exempt Securities:
                    </span>{' '}
                    Municipal bonds and other tax-exempt investments.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Other Current Assets:
                    </span>{' '}
                    Any other short-term assets (attach statement).
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Loans to Shareholders:
                    </span>{' '}
                    Amounts loaned to shareholders.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Mortgage and Real Estate Loans:
                    </span>{' '}
                    Loans made secured by property.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Other Investments:
                    </span>{' '}
                    Additional investment assets (itemize).
                  </li>
                  <li>
                    <ul style={{ listStyleType: 'lower-alpha' }}>
                      <li>
                        {' '}
                        <span style={{ fontWeight: 600 }}>
                          Buildings and Other Depreciable Assets:
                        </span>{' '}
                        Cost of depreciable property before depreciation.
                      </li>
                      <li>
                        <span style={{ fontWeight: 600 }}>
                          Accumulated Depreciation:
                        </span>{' '}
                        Total depreciation taken.
                      </li>
                    </ul>
                  </li>

                  <li>
                    <ul style={{ listStyleType: 'lower-alpha' }}>
                      <li>
                        {' '}
                        <span style={{ fontWeight: 600 }}>
                          Depletable Assets:
                        </span>{' '}
                        Assets subject to depletion (e.g., natural resources).
                      </li>
                      <li>
                        <span style={{ fontWeight: 600 }}>
                          Accumulated Depletion:
                        </span>{' '}
                        Total depletion taken.
                      </li>
                    </ul>
                  </li>

                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>Land:</span> Value of land
                    owned (net of amortization).
                  </li>
                  <li>
                    <ul style={{ listStyleType: 'lower-alpha' }}>
                      <li>
                        {' '}
                        <span style={{ fontWeight: 600 }}>
                          Intangible Assets:
                        </span>{' '}
                        Amortizable assets such as patents.
                      </li>
                      <li>
                        <span style={{ fontWeight: 600 }}>
                          Accumulated Amortization:
                        </span>{' '}
                        Total amortization taken.
                      </li>
                    </ul>
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>Other Assets:</span>{' '}
                    Miscellaneous assets not listed above (attach statement).
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>Total Assets:</span> Sum
                    of all asset categories.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Liabilities and Shareholders’ Equity
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-liabilities-img.webp" />
                  <source srcSet="/assets/images/1120s-liabilities-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-liabilities-img.png"
                    loading="lazy"
                    alt="Liabilities and Shareholders’ Equity"
                    width={757}
                    height={221}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report amounts for liabilities and equity similarly at the
                  beginning and end of the tax year:
                </Typography>
                <Box
                  component="ol"
                  start={16}
                  sx={{ mb: 4, '& li': { mb: 1.2 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}> Accounts Payable:</span>{' '}
                    Amounts owed to vendors/suppliers.
                  </li>
                  <li>
                    Mortgages, Notes, Bonds Payable (due in less than 1 year).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Other Current Liabilities:
                    </span>{' '}
                    Any other short-term liabilities (attach statement).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Loans from Shareholders:
                    </span>{' '}
                    Debt owed to shareholders.{' '}
                  </li>
                  <li>
                    Mortgages, Notes, Bonds Payable (due in 1 year or more).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Other Liabilities:</span>{' '}
                    Long-term liabilities not included above (attach
                    statement).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Capital Stock:</span>{' '}
                    Value of issued stock at par or stated value.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Additional Paid-in Capital:
                    </span>{' '}
                    Amount received over par value from stock issuance.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Retained Earnings:</span>{' '}
                    Cumulative earnings retained in the corporation.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Adjustments to Shareholders’ Equity:
                    </span>{' '}
                    Includes unrealized gains/losses and other equity
                    adjustments (attach statement).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Cost of Treasury Stock:
                    </span>{' '}
                    Value of shares repurchased by the corporation.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Total Liabilities and Shareholders’ Equity:
                    </span>{' '}
                    Must equal total assets reported.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Filing Notes
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Many smaller S Corporations with total assets and receipts
                    under $250,000 may be exempt from filing Schedule L (see
                    Schedule B, Line 11).{' '}
                  </li>
                  <li>
                    Ensure Schedule L balances at the start and end of the year
                    reconcile with your income statement and other schedules
                    (M-1 and M-2).{' '}
                  </li>
                  <li>
                    Attach statements to explain any discrepancies or unusual
                    balances.{' '}
                  </li>
                  <li>
                    Accurate completion of Schedule L increases compliance,
                    reduces audit risk, and clarifies the financial condition of
                    your S Corporation.{' '}
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schm1"
                >
                  Schedule M-1 – Reconciliation of Income (Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule M-1 is used to reconcile the corporation’s net income
                  or loss reported on its financial accounting books with the
                  taxable income or loss reported on the tax return. This
                  reconciliation is necessary because differences often arise
                  between accounting income and taxable income due to
                  differences in tax law and accounting principles.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Schedule M-1 Line-by-Line Explanation with Example
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-schm1-img.webp" />
                  <source srcSet="/assets/images/1120s-schm1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-schm1-img.png"
                    loading="lazy"
                    alt="Schedule M-1 Line-by-Line Explanation with Example"
                    width={751}
                    height={252}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 1 – Net income (loss) per books
                    </span>
                    <br />
                    Enter the company’s net income or loss according to its
                    accounting records.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      Example:{' '}
                    </span>
                    $100,000 net income per books.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 2 – Income included on Schedule K but not recorded on
                      books this year
                    </span>
                    <br />
                    Report income items that appear on Schedule K but were not
                    recorded in the accounting books for the current tax year
                    (itemize).
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      Example:{' '}
                    </span>
                    $5,000 of tax-exempt interest.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 3 – Expenses recorded on books this year not included
                      on Schedule K
                    </span>
                    <br />
                    Include expenses recorded in the books but not deductible
                    for tax purposes (itemize).
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      Example:{' '}
                    </span>
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Depreciation per books:{' '}
                    </span>
                    $8,000
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Travel and entertainment expenses disallowed:{' '}
                    </span>
                    $2,000
                    <br />
                    <span style={{ fontWeight: 600 }}> Total:</span> $10,000
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 4 – Add lines 1 through 3
                    </span>
                    <br />
                    Sum the amounts from Lines 1, 2, and 3.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      Example:{' '}
                    </span>
                    $100,000 + $5,000 + $10,000 = $115,000.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 5 – Income recorded on books this year not included
                      on Schedule K
                    </span>
                    <br />
                    Report income recorded on books but excluded from taxable
                    income (e.g., tax-exempt interest).
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      Example:{' '}
                    </span>
                    $5,000 tax-exempt interest.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 6 – Deductions on Schedule K not charged against book
                      income
                    </span>
                    <br />
                    Include deductions reported on Schedule K but not deducted
                    as expenses on the books (itemize).
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      Example:{' '}
                    </span>
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Tax depreciation:</span>{' '}
                    $12,000.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 7 – Add lines 5 and 6
                    </span>
                    <br />
                    Sum Lines 5 and 6.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      Example:{' '}
                    </span>
                    $5,000 + $12,000 = $17,000.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Line 8 – Income (loss) per Schedule K, line 18
                    </span>
                    <br />
                    Subtract Line 7 from Line 4 to find the taxable income or
                    loss reported on Schedule K.
                    <br />
                    <span style={{ fontWeight: 600, fontStyle: 'italic' }}>
                      Example:{' '}
                    </span>
                    $115,000 – $17,000 = $98,000 taxable income.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Purpose and Importance
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Schedule M-1 helps the IRS verify that your corporation has
                  accurately reported taxable income by explaining differences
                  between accounting and tax figures. Completing it thoroughly
                  reduces audit risks and ensures compliance with tax laws.{' '}
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schm2"
                >
                  Schedule M-2 – Analysis of Accumulated Adjustments Account
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule M-2 tracks changes in your S Corporation’s
                  accumulated adjustments account and other related equity
                  accounts throughout the tax year. These accounts reflect the
                  corporation’s undistributed taxable income previously taxed to
                  shareholders, accumulated earnings and profits, and other
                  adjustments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Completing Schedule M-2 helps shareholders accurately
                  calculate their basis and determine the tax treatment of
                  distributions and income items.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Line-by-Line Explanation{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1120s-schm2-img.webp" />
                  <source srcSet="/assets/images/1120s-schm2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/1120s-schm2-img.png"
                    loading="lazy"
                    alt=" Line-by-Line Explanation"
                    width={762}
                    height={255}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Line 1 – Balance at Beginning of Tax Year:
                    </span>{' '}
                    <br />
                    Enter the balance of the AAA, undistributed taxable income
                    previously taxed, accumulated earnings and profits, and
                    other adjustments account at the beginning of the tax year.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Line 2 – Ordinary Income from Page 1, Line 22:
                    </span>{' '}
                    <br />
                    Report the corporation’s ordinary business income (or loss)
                    for the year.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Line 3 – Other Additions:
                    </span>{' '}
                    <br />
                    Include other increases such as tax-exempt income,
                    non-deductible expenses that increase AAA, or other relevant
                    adjustments.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Line 4 – Loss from Page 1, Line 22:
                    </span>{' '}
                    <br />
                    Enter any ordinary business loss for the year.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Line 5 – Other Reductions:
                    </span>{' '}
                    <br />
                    Include decreases such as non-dividend distributions,
                    non-deductible expenses that reduce AAA, or other relevant
                    items.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Line 6 – Combine Lines 1 through 5:
                    </span>{' '}
                    <br />
                    Add all additions and subtract reductions from the beginning
                    balance to compute the adjusted balance before
                    distributions.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Line 7 – Distributions:
                    </span>{' '}
                    <br />
                    Enter total distributions to shareholders that reduce the
                    AAA or related accounts.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Line 8 – Balance at End of Tax Year:
                    </span>{' '}
                    <br />
                    Subtract Line 7 from Line 6 to arrive at the ending balance
                    for each account. This balance carries forward to the next
                    tax year.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Example Summary
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Suppose an S Corporation has the following for its AAA
                    account:{' '}
                  </li>
                  <li>Beginning balance (Line 1): $50,000 </li>
                  <li>Ordinary income (Line 2): $35,000 </li>
                  <li>Other additions (Line 3): $5,000 </li>
                  <li>Losses (Line 4): $0 </li>
                  <li>Other reductions (Line 5): $2,000 </li>
                  <li>Distributions (Line 7): $20,000 </li>
                  <li>
                    Calculate as follows: <br />
                    Line 6 = $50,000 + $35,000 + $5,000 – $0 – $2,000 = $88,000
                    <br />
                    Line 8 = $88,000 – $20,000 = $68,000 ending balance
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important Notes
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Filing Schedule M-2 is required if the corporation’s total
                    assets or receipts exceed $250,000 or if the corporation was
                    previously a C corporation.{' '}
                  </li>
                  <li>
                    Accurate maintenance of these accounts is critical for
                    determining the tax treatment of shareholder distributions
                    and for basis calculations on Schedule K-1.{' '}
                  </li>
                  <li>
                    Related schedules (L and M-1) should reconcile with the
                    amounts reported on Schedule M-2.{' '}
                  </li>
                  <li>
                    Attach clear supporting statements explaining “Other
                    Additions” and “Other Reductions” with appropriate
                    documentation.{' '}
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="common"
                >
                  Common Mistakes to Avoid
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li style={{ fontWeight: 600 }}>
                    Incorrect Ownership Percentages{' '}
                  </li>
                  <ul>
                    <li>Always update shareholder changes immediately. </li>
                  </ul>
                  <li style={{ fontWeight: 600 }}>
                    Mixing Business & Personal Expenses
                  </li>
                  <ul>
                    <li>Deduct only legitimate business expenses. </li>
                  </ul>
                  <li style={{ fontWeight: 600 }}>Late K-1 Distribution</li>
                  <ul>
                    <li>Leads to $310 per-K-1 penalty. </li>
                  </ul>
                  <li style={{ fontWeight: 600 }}>Not Filing Extensions</li>
                  <ul>
                    <li>Missing deadlines costs $235/shareholder/month. </li>
                  </ul>
                  <li style={{ fontWeight: 600 }}>Inconsistent Records </li>
                  <ul>
                    <li>Schedule L must match corporate financials. </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Lesser-Known S Corp Filing Add-Ons{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Many S Corps miss filing related forms alongside 1120-S, which
                  can result in IRS follow-ups:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-1125-a"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 1125-A
                    </Link>{' '}
                    – Cost of Goods Sold (for product businesses){' '}
                  </li>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-4562"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 4562
                    </Link>{' '}
                    – Depreciation and Amortization{' '}
                  </li>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-4797"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 4797
                    </Link>{' '}
                    – Sale of Business Property{' '}
                  </li>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-8949"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 8949
                    </Link>{' '}
                    - Reports sales of capital assets in detail.{' '}
                  </li>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-3800"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 3800
                    </Link>{' '}
                    - General Business Credit to claim various credits.{' '}
                  </li>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-8825"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 8825
                    </Link>{' '}
                    - Rental real estate income and expenses.
                  </li>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-8938"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 8938
                    </Link>{' '}
                    - Discloses specified foreign financial assets.{' '}
                  </li>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-8996"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 8996
                    </Link>{' '}
                    - Qualified Opportunity Fund elections and reporting.{' '}
                  </li>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-8941"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 8941
                    </Link>{' '}
                    - Small Employer Health Insurance Premiums Credit.{' '}
                  </li>
                </Box>
                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 2, mb: 3 }}
                >
                  <b>Quick Tip:</b> TaxZerone automatically checks for
                  applicable forms so you don’t miss a requirement.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Common Filing Traps - And How to Avoid Them{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Mixing Personal and Business Expenses:
                    </span>{' '}
                    Maintain separate accounts.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Wrong Ownership Percentages:
                    </span>{' '}
                    Double-check corporate records.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Ignoring State Requirements:
                    </span>{' '}
                    Some states require their own S Corp returns.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Digital Document Management with ZeroneVault
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  With{' '}
                  <Link
                    href="https://taxzerone.zeronevault.com/"
                    sx={{ color: '#0202ff' }}
                  >
                    ZeroneVault
                  </Link>
                  , TaxZerone’s secure cloud portal, you can store:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Past tax returns </li>
                  <li>Schedule K-1s </li>
                  <li>Supporting schedules and receipts </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This not only simplifies annual filing but also protects you
                  during audits.{' '}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="why"
                >
                  Why File Form 1120-S Online with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Filing manually can be stressful and error-prone. That’s where
                  <span style={{ fontWeight: 600 }}> TaxZerone</span>, an
                  IRS-authorized e-file provider, simplifies the process.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of E-Filing with TaxZerone
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Real-time error checks
                    </span>{' '}
                    – Reduce IRS rejections.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Instant Schedule K-1 generation
                    </span>{' '}
                    – Deliver to shareholders immediately.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Secure cloud storage (ZeroneVault)
                    </span>{' '}
                    – Keep returns and K-1s safe.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Faster IRS acceptance
                    </span>{' '}
                    – No mailing delays.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Affordable pricing</span>{' '}
                    – Ideal for small businesses.{' '}
                  </li>
                </Box>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Skip the stress of paperwork. Get compliance, accuracy, and
                    peace of mind - all at an affordable price.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120sinstructions`}
                  >
                    Start Filing 1120-S Online
                  </Button>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="faq"
                >
                  Frequently Asked Questions (FAQs)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here’s a collection of{' '}
                  <span style={{ fontWeight: 600 }}>unique, detailed FAQs</span>{' '}
                  for the instruction page (different from landing page
                  FAQs).{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  1. Do all S Corps need to file Form 1120-S every year?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Yes. Even if your S Corp had no income or activity, the IRS
                  requires annual filing to maintain S Corporation status.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  2. Can an S Corp have a loss on Form 1120-S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Yes. Losses pass through to shareholders and may offset other
                  personal income, subject to{' '}
                  <span style={{ fontWeight: 600 }}>
                    basis and at-risk rules
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  3. What happens if I miss the March 15 deadline?
                </Typography>
                <Typography
                  component="ul"
                  variant="body1"
                  sx={{ mb: 2, '& li': { mb: 1.2 } }}
                >
                  <li>
                    A late filing penalty of{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      $245 per shareholder per month
                    </span>{' '}
                    applies, and if the return is{' '}
                    <span style={{ fontWeight: 600 }}>
                      more than 60 days late,
                    </span>{' '}
                    the{' '}
                    <span style={{ fontWeight: 600 }}>
                      minimum penalty is $525 or the tax due, whichever is
                      smaller.
                    </span>
                  </li>
                  <li>
                    File <span style={{ fontWeight: 600 }}>Form 7004</span> to
                    request a 6-month extension.{' '}
                  </li>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  4. How do shareholder K-1s connect to Form 1120-S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule K summarizes total results, while{' '}
                  <span style={{ fontWeight: 600 }}>
                    each shareholder’s K-1 breaks down their share
                  </span>
                  . Errors in 1120-S flow directly into K-1s and personal tax
                  returns.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  5. Can single-member LLCs file Form 1120-S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  No. A single-member LLC is typically a disregarded entity. To
                  file 1120 s form, it must elect S Corporation status with{' '}
                  <span style={{ fontWeight: 600 }}>Form 2553</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  6. Do states accept IRS Form 1120-S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Some states do, but others require{' '}
                  <span style={{ fontWeight: 600 }}>
                    separate state S Corp returns
                  </span>
                  . Always check your state’s rules.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  7. Is e-filing mandatory for Form 1120-S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Yes, for most corporations with{' '}
                  <span style={{ fontWeight: 600 }}>10+ returns,</span> but the
                  IRS encourages e-filing for all filers. Platforms like{' '}
                  <span style={{ fontWeight: 600 }}>TaxZerone</span> make it
                  easier and faster.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  8.Where to mail form 1120 s?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The{' '}
                  <Link
                    href="/resources/business-tax/form-1120-s-mailing-address/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    mailing address for Form 1120-S
                  </Link>{' '}
                  depends on your business location and whether you are
                  including a payment with the return. The IRS provides
                  different mailing addresses based on the state where the
                  corporation is located.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b> Instead of mailing, you can{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1120-S
                  </Link>{' '}
                  with TaxZerone for a faster, secure, and more convenient
                  filing experience.
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Filing your Form 1120-S doesn’t need to be overwhelming.
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              With TaxZerone, you get:
            </Typography>
            <Box sx={{ maxWidth: 450, textAlign: 'left', m: 'auto' }}>
              <Box
                component="ul"
                sx={{
                  mb: 2,
                  '& li': { mb: 1.2, listStyleType: 'none', display: 'block' },
                }}
              >
                <li>
                  <CheckCircleIcon sx={{ mb: -0.7, color: '#1da94a' }} />{' '}
                  IRS-authorized e-filing
                </li>
                <li>
                  <CheckCircleIcon sx={{ mb: -0.7, color: '#1da94a' }} />{' '}
                  Real-time error checks
                </li>
                <li>
                  <CheckCircleIcon sx={{ mb: -0.7, color: '#1da94a' }} />{' '}
                  Automatic Schedule K-1 form 1120-s generation
                </li>
                <li>
                  <CheckCircleIcon sx={{ mb: -0.7, color: '#1da94a' }} /> Secure
                  cloud storage (
                  <Link
                    href="https://taxzerone.zeronevault.com/"
                    sx={{ color: '#0202ff' }}
                  >
                    ZeroneVault
                  </Link>
                  )
                </li>
                <li>
                  <CheckCircleIcon sx={{ mb: -0.7, color: '#1da94a' }} />{' '}
                  Affordable pricing for businesses of all sizes
                </li>
              </Box>
            </Box>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120sinstructions`}
              >
                Start Your 1120-S Filing Today
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1120SInstruction;
